import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useIsMobile } from "@/hooks/use-mobile";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Ruler,
  FileText,
  Image as ImageIcon,
  Search,
  ChevronDown,
  MoveHorizontal,
  Grid2X2,
  Hash,
  Layers,
} from "lucide-react";
import { format } from "date-fns";
import { TakeoffViewer } from "./TakeoffViewer";
import {
  LINEAR_UNITS,
  linearUnitLabel,
  areaUnitLabel,
} from "./takeoff-helpers";
import { cn } from "@/lib/utils";
import { useTakeoffDocuments, useUploadTakeoffDocument } from "@/hooks/useTakeoffDocuments";
import { useTakeoffGroups } from "@/hooks/useTakeoffGroups";
import { TakeoffGroupsPanel } from "./TakeoffGroupsPanel";
import { TakeoffDrilldownSheet } from "./TakeoffDrilldownSheet";
import { aggregateMeasurements, type RawMeasurement, type DrilldownAnnotation } from "@/lib/takeoffAggregation";
import type { TakeoffOwnerType } from "@/hooks/useDocumentAnnotations";

export interface TakeoffWorkspaceProps {
  ownerType: TakeoffOwnerType;
  ownerId: string;
  initialDocumentId?: string | null;
  readOnly?: boolean;
}

function isTakeoffCompatible(mimeType: string | null): boolean {
  if (!mimeType) return false;
  return mimeType.includes("pdf") || mimeType.startsWith("image");
}

function isPdfType(mimeType: string | null): boolean {
  return !!mimeType && mimeType.includes("pdf");
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const MEASUREMENT_TYPES = [
  "measurement_linear",
  "measurement_area",
  "measurement_count",
] as const;

export const TakeoffWorkspace = ({
  ownerType,
  ownerId,
  initialDocumentId,
  readOnly = false,
}: TakeoffWorkspaceProps) => {
  const isMobile = useIsMobile();
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null);
  const [searchFilter, setSearchFilter] = useState("");
  const [displayUnit, setDisplayUnit] = useState("ft");
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [groupsPanelOpen, setGroupsPanelOpen] = useState(true);
  const [activeGroupId, setActiveGroupId] = useState<string | null>(null);
  const [hiddenGroupIds, setHiddenGroupIds] = useState<Set<string>>(new Set());
  const [drilldownMetric, setDrilldownMetric] = useState<"linear" | "area" | "count" | null>(null);
  const [focusAnnotation, setFocusAnnotation] = useState<{ docId: string; pageNumber: number; annotationId: string } | null>(null);
  const deepLinkUsed = useRef(false);

  // ─── Normalize context once ───
  const isEstimate = ownerType === "estimate";
  const docQueryOpts = isEstimate ? { estimateId: ownerId } : { projectId: ownerId };
  const groupQueryOpts = isEstimate ? { estimateId: ownerId } : { projectId: ownerId };
  const fkColumn = isEstimate ? "estimate_document_id" : "project_document_id";

  // ─── Documents ───
  const { data: documents = [], isLoading } = useTakeoffDocuments(docQueryOpts);
  const docIds = useMemo(() => documents.map((d) => d.id), [documents]);

  // ─── Document map for drilldown ───
  const documentMap = useMemo(() => {
    const map: Record<string, string> = {};
    documents.forEach((d) => { map[d.id] = d.file_name; });
    return map;
  }, [documents]);

  // ─── Groups ───
  const { data: groups = [] } = useTakeoffGroups(groupQueryOpts);

  // ─── Annotation summary (batch) — enriched for drilldown ───
  const { data: rawAnnotationsEnriched = [] } = useQuery({
    queryKey: ["takeoff_summary_annotations", ownerType, docIds],
    queryFn: async () => {
      if (docIds.length === 0) return [];
      const { data, error } = await (supabase as any)
        .from("document_annotations")
        .select(`id, ${fkColumn}, annotation_type, measured_value, unit, takeoff_group_id, label, color, page_number, created_at, source_estimate_annotation_id`)
        .in(fkColumn, docIds)
        .in("annotation_type", ["measurement_linear", "measurement_area", "measurement_count"]);
      if (error) throw error;
      return (data ?? []) as any[];
    },
    enabled: docIds.length > 0,
  });

  // Split into RawMeasurement (for aggregation) and DrilldownAnnotation (for UI)
  const rawAnnotations: (RawMeasurement & Record<string, any>)[] = rawAnnotationsEnriched;

  const drilldownAnnotations: DrilldownAnnotation[] = useMemo(() => {
    return rawAnnotationsEnriched.map((r: any) => ({
      id: r.id,
      label: r.label,
      color: r.color || "#EF4444",
      page_number: r.page_number,
      created_at: r.created_at,
      annotation_type: r.annotation_type,
      measured_value: r.measured_value,
      unit: r.unit,
      takeoff_group_id: r.takeoff_group_id,
      source_estimate_annotation_id: r.source_estimate_annotation_id,
      document_id: r[fkColumn],
    }));
  }, [rawAnnotationsEnriched, fkColumn]);

  // ─── Annotation counts per document ───
  const annotationCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    rawAnnotations.forEach((r: any) => {
      const docId = r[fkColumn];
      counts[docId] = (counts[docId] || 0) + 1;
    });
    return counts;
  }, [rawAnnotations, fkColumn]);

  // ─── Aggregation using shared helper ───
  const workspaceSummary = useMemo(
    () => aggregateMeasurements(rawAnnotations, displayUnit),
    [rawAnnotations, displayUnit]
  );

  // ─── Scale status batch query ───
  const { data: scaleMap = new Map<string, Set<number>>() } = useQuery({
    queryKey: ["takeoff_scales_batch", ownerType, docIds],
    queryFn: async () => {
      if (docIds.length === 0) return new Map<string, Set<number>>();
      const { data, error } = await supabase
        .from("document_scales")
        .select(`${fkColumn}, page_number`)
        .in(fkColumn, docIds);
      if (error) throw error;
      const map = new Map<string, Set<number>>();
      (data ?? []).forEach((s: any) => {
        const docId = s[fkColumn];
        if (!map.has(docId)) map.set(docId, new Set());
        map.get(docId)!.add(s.page_number);
      });
      return map;
    },
    enabled: docIds.length > 0,
  });

  // ─── Sort: PDFs first, then images ───
  const sortedDocuments = useMemo(() => {
    return [...documents].sort((a, b) => {
      const aPdf = isPdfType(a.mime_type) ? 0 : 1;
      const bPdf = isPdfType(b.mime_type) ? 0 : 1;
      if (aPdf !== bPdf) return aPdf - bPdf;
      return (annotationCounts[b.id] || 0) - (annotationCounts[a.id] || 0);
    });
  }, [documents, annotationCounts]);

  const filteredDocuments = useMemo(() => {
    if (!searchFilter.trim()) return sortedDocuments;
    const lower = searchFilter.toLowerCase();
    return sortedDocuments.filter((d) => d.file_name.toLowerCase().includes(lower));
  }, [sortedDocuments, searchFilter]);

  // ─── Selection logic ───
  useEffect(() => {
    if (documents.length === 0) return;
    if (!deepLinkUsed.current && initialDocumentId && documents.find((d) => d.id === initialDocumentId)) {
      setSelectedDocId(initialDocumentId);
      deepLinkUsed.current = true;
      return;
    }
    if (!selectedDocId || !documents.find((d) => d.id === selectedDocId)) {
      setSelectedDocId(sortedDocuments[0]?.id ?? null);
    }
  }, [documents, initialDocumentId, sortedDocuments]);

  const selectedDoc = documents.find((d) => d.id === selectedDocId);

  const getScaleStatus = (docId: string): "calibrated" | "none" => {
    const pages = scaleMap.get(docId);
    if (!pages || pages.size === 0) return "none";
    return "calibrated";
  };

  const toggleGroupVisibility = useCallback((groupId: string) => {
    setHiddenGroupIds((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  }, []);

  // ─── Annotation click handler — switch doc and focus ───
  const handleAnnotationClick = useCallback(
    (params: { docId: string; pageNumber: number; annotationId: string }) => {
      // Switch to the target document if needed
      if (params.docId !== selectedDocId) {
        setSelectedDocId(params.docId);
      }
      setFocusAnnotation(params);
      // Close the drilldown sheet
      setDrilldownMetric(null);
    },
    [selectedDocId]
  );

  if (isLoading) {
    return <p className="text-center text-muted-foreground py-12">Loading plans…</p>;
  }

  if (documents.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-muted-foreground gap-3">
        <Ruler className="h-12 w-12 opacity-20" />
        <p className="text-sm font-medium">No plans uploaded yet</p>
        <p className="text-xs">
          Upload PDF or image drawings in the <strong>Documents</strong> tab to use Plans & Takeoff.
        </p>
      </div>
    );
  }

  // ─── Workspace Header ───
  const workspaceHeader = (
    <div className="flex flex-wrap items-center gap-2 px-3 py-1.5 border-b border-border bg-card/80 backdrop-blur-sm">
      <div className="flex items-center gap-3 flex-wrap">
        <button
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-muted/60 text-xs hover:bg-muted transition-colors cursor-pointer"
          onClick={() => setDrilldownMetric("linear")}
        >
          <MoveHorizontal className="h-3.5 w-3.5 text-primary" />
          <span className="text-muted-foreground">Linear</span>
          <span className="font-semibold text-foreground tabular-nums">
            {workspaceSummary.totalLinear.toFixed(1)} {linearUnitLabel(displayUnit)}
          </span>
        </button>
        <button
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-muted/60 text-xs hover:bg-muted transition-colors cursor-pointer"
          onClick={() => setDrilldownMetric("area")}
        >
          <Grid2X2 className="h-3.5 w-3.5 text-primary" />
          <span className="text-muted-foreground">Area</span>
          <span className="font-semibold text-foreground tabular-nums">
            {workspaceSummary.totalArea.toFixed(1)} {areaUnitLabel(displayUnit)}
          </span>
        </button>
        <button
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-muted/60 text-xs hover:bg-muted transition-colors cursor-pointer"
          onClick={() => setDrilldownMetric("count")}
        >
          <Hash className="h-3.5 w-3.5 text-primary" />
          <span className="text-muted-foreground">Count</span>
          <span className="font-semibold text-foreground tabular-nums">
            {workspaceSummary.totalCount}
          </span>
        </button>
      </div>

      <div className="flex items-center gap-2 ml-auto">
        {readOnly && (
          <Badge variant="outline" className="text-[10px] bg-amber-100 text-amber-800">Read-Only</Badge>
        )}
        {selectedDoc && (
          <>
            <span className="text-xs text-muted-foreground truncate max-w-[200px] hidden md:inline">
              {selectedDoc.file_name}
            </span>
            <div
              className={cn(
                "h-2 w-2 rounded-full shrink-0",
                getScaleStatus(selectedDoc.id) === "calibrated" ? "bg-green-500" : "bg-muted-foreground/40"
              )}
              title={getScaleStatus(selectedDoc.id) === "calibrated" ? "Scale calibrated" : "Not calibrated"}
            />
          </>
        )}
        <Select value={displayUnit} onValueChange={setDisplayUnit}>
          <SelectTrigger className="h-7 w-16 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            {LINEAR_UNITS.map((u) => (
              <SelectItem key={u} value={u} className="text-xs">{u}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {!isMobile && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => setGroupsPanelOpen(!groupsPanelOpen)}
            title="Toggle Groups Panel"
          >
            <Layers className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </div>
  );

  // ─── Drawing Navigator Item ───
  const renderDrawingItem = (doc: typeof documents[0]) => {
    const count = annotationCounts[doc.id] || 0;
    const isActive = doc.id === selectedDocId;
    const status = getScaleStatus(doc.id);
    const isPdf = isPdfType(doc.mime_type);

    return (
      <button
        key={doc.id}
        onClick={() => setSelectedDocId(doc.id)}
        className={cn(
          "w-full text-left rounded-md px-3 py-2.5 text-sm transition-colors relative",
          isActive
            ? "bg-primary/10 border-l-2 border-l-primary border border-primary/20"
            : "hover:bg-muted border border-transparent"
        )}
      >
        <div className="flex items-start gap-2.5">
          {isPdf ? (
            <FileText className="h-4 w-4 shrink-0 text-red-500 mt-0.5" />
          ) : (
            <ImageIcon className="h-4 w-4 shrink-0 text-blue-500 mt-0.5" />
          )}
          <div className="flex-1 min-w-0">
            <span className="font-medium truncate text-xs block">{doc.file_name}</span>
            <div className="flex items-center gap-2 mt-1 text-[10px] text-muted-foreground">
              <span>{formatFileSize(doc.file_size)}</span>
              <span>·</span>
              <span>{format(new Date(doc.created_at), "MMM d, yyyy")}</span>
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {count > 0 && (
              <Badge variant="secondary" className="text-[10px] gap-1 px-1.5 py-0">
                <Ruler className="h-2.5 w-2.5" />{count}
              </Badge>
            )}
            <div
              className={cn(
                "h-2 w-2 rounded-full",
                status === "calibrated" ? "bg-green-500" : "bg-muted-foreground/30"
              )}
              title={status === "calibrated" ? "Scale calibrated" : "Not calibrated"}
            />
          </div>
        </div>
      </button>
    );
  };

  // ─── Mobile Layout ───
  if (isMobile) {
    return (
      <div className="space-y-2">
        <Collapsible open={summaryOpen} onOpenChange={setSummaryOpen}>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="w-full justify-between text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5"><Ruler className="h-3.5 w-3.5" />Totals</span>
              <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", summaryOpen && "rotate-180")} />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="flex gap-2 px-2 pb-2">
              <button className="flex-1 rounded-md bg-muted/60 px-2 py-1.5 text-center hover:bg-muted transition-colors" onClick={() => setDrilldownMetric("linear")}>
                <p className="text-[10px] text-muted-foreground">Linear</p>
                <p className="text-xs font-semibold tabular-nums">{workspaceSummary.totalLinear.toFixed(1)} {linearUnitLabel(displayUnit)}</p>
              </button>
              <button className="flex-1 rounded-md bg-muted/60 px-2 py-1.5 text-center hover:bg-muted transition-colors" onClick={() => setDrilldownMetric("area")}>
                <p className="text-[10px] text-muted-foreground">Area</p>
                <p className="text-xs font-semibold tabular-nums">{workspaceSummary.totalArea.toFixed(1)} {areaUnitLabel(displayUnit)}</p>
              </button>
              <button className="flex-1 rounded-md bg-muted/60 px-2 py-1.5 text-center hover:bg-muted transition-colors" onClick={() => setDrilldownMetric("count")}>
                <p className="text-[10px] text-muted-foreground">Count</p>
                <p className="text-xs font-semibold tabular-nums">{workspaceSummary.totalCount}</p>
              </button>
            </div>
          </CollapsibleContent>
        </Collapsible>

        <ScrollArea className="max-h-40">
          <div className="flex gap-2 pb-2 px-1 overflow-x-auto">
            {sortedDocuments.map((doc) => (
              <Button
                key={doc.id}
                variant={doc.id === selectedDocId ? "default" : "outline"}
                size="sm"
                className="shrink-0 text-xs gap-1.5"
                onClick={() => setSelectedDocId(doc.id)}
              >
                <div className={cn("h-1.5 w-1.5 rounded-full shrink-0", getScaleStatus(doc.id) === "calibrated" ? "bg-green-500" : "bg-muted-foreground/30")} />
                <span className="truncate max-w-[120px]">{doc.file_name}</span>
                {(annotationCounts[doc.id] || 0) > 0 && (
                  <Badge variant="secondary" className="ml-0.5 text-[10px] px-1 py-0">{annotationCounts[doc.id]}</Badge>
                )}
              </Button>
            ))}
          </div>
        </ScrollArea>

        {selectedDoc && (
          <div className="rounded-xl border bg-card overflow-hidden" style={{ height: "calc(100vh - 220px)" }}>
            <TakeoffViewer
              key={selectedDocId}
              mode="embedded"
              documentId={selectedDoc.id}
              filePath={selectedDoc.file_path}
              fileName={selectedDoc.file_name}
              mimeType={selectedDoc.mime_type}
              ownerType={ownerType}
              readOnly={readOnly}
              activeGroupId={activeGroupId}
              hiddenGroupIds={hiddenGroupIds}
              focusAnnotation={focusAnnotation}
              onFocusHandled={() => setFocusAnnotation(null)}
            />
          </div>
        )}

        {/* Drilldown Sheet */}
        <TakeoffDrilldownSheet
          open={drilldownMetric !== null}
          onOpenChange={(open) => { if (!open) setDrilldownMetric(null); }}
          metric={drilldownMetric}
          drilldownAnnotations={drilldownAnnotations}
          groups={groups}
          documentMap={documentMap}
          displayUnit={displayUnit}
          ownerType={ownerType}
          hiddenGroupIds={hiddenGroupIds}
          onAnnotationClick={handleAnnotationClick}
        />
      </div>
    );
  }

  // ─── Desktop Layout ───
  return (
    <div className="space-y-0 rounded-xl border bg-card overflow-hidden">
      {workspaceHeader}

      <div className="flex overflow-hidden" style={{ height: "calc(100vh - 220px)" }}>
        {/* Drawing Navigator */}
        <div className="w-48 shrink-0 border-r border-border flex flex-col bg-card">
          <div className="px-3 py-2.5 border-b border-border space-y-2">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Drawings ({sortedDocuments.length})
            </h3>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
              <Input
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                placeholder="Filter drawings…"
                className="h-7 pl-7 text-xs"
              />
            </div>
          </div>
          <ScrollArea className="flex-1">
            <div className="p-1.5 space-y-0.5">
              {filteredDocuments.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-6">No matching drawings</p>
              ) : (
                filteredDocuments.map(renderDrawingItem)
              )}
            </div>
          </ScrollArea>
        </div>

        {/* Viewer */}
        <div className="flex-1 overflow-hidden">
          {selectedDoc ? (
            <TakeoffViewer
              key={selectedDocId}
              mode="embedded"
              documentId={selectedDoc.id}
              filePath={selectedDoc.file_path}
              fileName={selectedDoc.file_name}
              mimeType={selectedDoc.mime_type}
              ownerType={ownerType}
              readOnly={readOnly}
              activeGroupId={activeGroupId}
              hiddenGroupIds={hiddenGroupIds}
              focusAnnotation={focusAnnotation}
              onFocusHandled={() => setFocusAnnotation(null)}
            />
          ) : (
            <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
              Select a drawing to begin
            </div>
          )}
        </div>

        {/* Groups Panel */}
        {groupsPanelOpen && (
          <TakeoffGroupsPanel
            ownerType={ownerType}
            ownerId={ownerId}
            groups={groups}
            rawAnnotations={rawAnnotations}
            drilldownAnnotations={drilldownAnnotations}
            displayUnit={displayUnit}
            activeGroupId={activeGroupId}
            onActiveGroupChange={setActiveGroupId}
            hiddenGroupIds={hiddenGroupIds}
            onToggleGroupVisibility={toggleGroupVisibility}
            documentMap={documentMap}
            onAnnotationClick={handleAnnotationClick}
            readOnly={readOnly}
          />
        )}
      </div>

      {/* Drilldown Sheet */}
      <TakeoffDrilldownSheet
        open={drilldownMetric !== null}
        onOpenChange={(open) => { if (!open) setDrilldownMetric(null); }}
        metric={drilldownMetric}
        drilldownAnnotations={drilldownAnnotations}
        groups={groups}
        documentMap={documentMap}
        displayUnit={displayUnit}
        ownerType={ownerType}
        hiddenGroupIds={hiddenGroupIds}
        onAnnotationClick={handleAnnotationClick}
      />
    </div>
  );
};
